// Resource Levelling — pure, view-only computation. Runs entirely client-side against data
// already fetched via useIssues() (demand, and the set of people — each issue's Owner) and
// useUserLookup() (each owner's capacity) — see
// ProjectResourceLevellingTab.tsx. Nothing here is persisted; the distribution strategy is a
// view-level toggle recomputed on every render.
//
// Gantt-style layout: people run down the Y axis (rows), dates run across the X axis
// (columns) — same axis convention as ganttChart.ts's own day-by-day header (including keeping
// weekend/holiday columns visible rather than collapsing them, same reasoning as Gantt's
// weekend shading) — and each cell is that person's planned hours for that single day, as plain
// numbers rather than bars.

import type { IIssue, IUserRef } from '@pulse/shared';

/** One row of the grid — structurally satisfied by both an issue owner resolved against the user
 *  lookup (global capacity only) and an IProjectStaffingMember (which may also carry a
 *  per-project `allocatedHoursPerWeek` override, honored when present). */
export interface LevellingPerson extends IUserRef {
  weeklyCapacityHours: number;
  allocatedHoursPerWeek?: number | null;
}

export type DistributionStrategy = 'equalAcross' | 'fillStart' | 'fillEnd';

export interface UnscheduledIssue {
  issue: IIssue;
  ownerName: string;
}

function toDateOnly(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isWeekday(d: Date): boolean {
  const day = d.getDay();
  return day !== 0 && day !== 6;
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Mon–Fri and not a project holiday — the single "is this a working day" rule every function
 *  below funnels through. `holidayDates` is a set of ISO dates (Setup > Holidays, active only)
 *  and defaults to empty so every existing caller that doesn't pass one keeps today's
 *  weekend-only behavior unchanged. */
function isWorkingDay(d: Date, holidayDates: ReadonlySet<string>): boolean {
  return isWeekday(d) && !holidayDates.has(isoDate(d));
}

/** Mon–Fri days between `start` and `end`, inclusive of both ends, excluding any date in
 *  `holidayDates` (ISO strings — see `isWorkingDay`). */
export function getWorkingDays(start: Date, end: Date, holidayDates: ReadonlySet<string> = new Set()): Date[] {
  const days: Date[] = [];
  const cursor = toDateOnly(start);
  const last = toDateOnly(end);
  while (cursor <= last) {
    if (isWorkingDay(cursor, holidayDates)) days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

/** The Monday of the week containing `d`. */
export function getMondayOfWeek(d: Date): Date {
  const date = toDateOnly(d);
  const day = date.getDay(); // 0 = Sunday .. 6 = Saturday
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return date;
}

/** Distributes an issue's `originalEstimate` (hours) across working days under `strategy`.
 *  Returns a map of ISO date -> hours planned that day. Empty for issues missing either date
 *  or with no estimate — the caller buckets those as "Unscheduled" instead. `holidayDates`
 *  (Setup > Holidays, active only) are skipped the same way weekends are. */
export function distributeIssueHours(
  issue: Pick<IIssue, 'startDate' | 'dueDate' | 'originalEstimate'>,
  dailyCapacityHours: number,
  strategy: DistributionStrategy,
  holidayDates: ReadonlySet<string> = new Set(),
): Map<string, number> {
  const result = new Map<string, number>();
  const estimate = issue.originalEstimate ?? 0;
  if (estimate <= 0 || !issue.startDate || !issue.dueDate) return result;

  const start = toDateOnly(new Date(issue.startDate));
  const due = toDateOnly(new Date(issue.dueDate));
  if (due < start) return result;

  if (strategy === 'equalAcross') {
    const windowDays = getWorkingDays(start, due, holidayDates);
    if (windowDays.length === 0) {
      // The window contains no working day (e.g. a single weekend day) — park the whole
      // estimate on the start date rather than silently dropping it.
      result.set(isoDate(start), estimate);
      return result;
    }
    const perDay = estimate / windowDays.length;
    windowDays.forEach((d) => result.set(isoDate(d), perDay));
    return result;
  }

  // Fill from Start / Fill from End: walk day-by-day from the anchor at dailyCapacityHours/day,
  // overflowing past the window (never truncating) when the estimate doesn't fit in it.
  if (dailyCapacityHours <= 0) {
    // Zero effective capacity (e.g. an explicit 0-hour project override) has nowhere sane to
    // spread the load — park it on the anchor day rather than looping forever.
    result.set(isoDate(strategy === 'fillStart' ? start : due), estimate);
    return result;
  }

  let remaining = estimate;
  const step = strategy === 'fillStart' ? 1 : -1;
  const cursor = toDateOnly(strategy === 'fillStart' ? start : due);
  while (remaining > 1e-6) {
    if (isWorkingDay(cursor, holidayDates)) {
      const hours = Math.min(remaining, dailyCapacityHours);
      result.set(isoDate(cursor), hours);
      remaining -= hours;
    }
    cursor.setDate(cursor.getDate() + step);
  }
  return result;
}

export interface DailyColumn {
  /** ISO date. */
  date: string;
  dayOfMonth: number;
  weekdayLabel: string;
  monthLabel: string;
  isWeekend: boolean;
  isHoliday: boolean;
}

/** Every calendar day from `start` to `end` inclusive — the column axis (dates) for the
 *  Gantt-style grid. Weekends and project holidays stay in the list (flagged, for shading)
 *  rather than being omitted, same as Gantt keeps weekend columns visible instead of collapsing
 *  the calendar. */
export function buildDailyRange(start: Date, end: Date, holidayDates: ReadonlySet<string> = new Set()): DailyColumn[] {
  const days: DailyColumn[] = [];
  const cursor = toDateOnly(start);
  const last = toDateOnly(end);
  while (cursor <= last) {
    const iso = isoDate(cursor);
    days.push({
      date: iso,
      dayOfMonth: cursor.getDate(),
      weekdayLabel: cursor.toLocaleDateString('en-US', { weekday: 'short' }),
      monthLabel: cursor.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      isWeekend: !isWeekday(cursor),
      isHoliday: holidayDates.has(iso),
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

export interface GridCell {
  plannedHours: number;
  capacityHours: number;
  /** The issues contributing to `plannedHours`, sorted by hours descending — feeds the
   *  click-to-see-breakdown panel. */
  items: GridCellItem[];
}

export interface GridCellItem {
  issueId: string;
  issueKey: string;
  issueName: string;
  hours: number;
  /** Unset for `buildLevellingGrid`'s own items (always the project being viewed) — populated
   *  by the "All Projects" scope in ProjectResourceLevellingTab.tsx, whose items can belong to
   *  a different project than the one currently open. */
  projectId?: string;
  projectKey?: string;
}

export interface PersonRow {
  userId: string;
  name: string;
  avatarUrl?: string;
  /** Effective weekly capacity: the project's override if set, else the user's global default. */
  capacityHours: number;
  /** Aligned 1:1 with the `days` passed to `buildLevellingGrid`. */
  cells: GridCell[];
}

/** Builds the Gantt-style levelling grid: one row per person in `people` (`rows`), one column per
 *  date (the `days` passed in), each cell that person's planned hours for that single day
 *  (their own `originalEstimate` spread across Start–Due under `strategy`) alongside their
 *  capacity that day (0 on a weekend/holiday, else 1/5th of their weekly figure). Demand is
 *  counted against `owner` only, regardless of the issue's `status` — including terminal/Done
 *  issues, so the grid reflects the full estimated schedule an Owner's issues describe rather
 *  than only what's still open. Issues missing either `startDate` or `dueDate` are collected
 *  into `unscheduled` (grouped implicitly by `ownerName`) instead of being dropped.
 *  `holidayDates` (Setup > Holidays, active only) are treated as non-working days alongside
 *  weekends, for both demand distribution and capacity. */
export function buildLevellingGrid(
  issues: IIssue[],
  people: readonly LevellingPerson[],
  days: readonly DailyColumn[],
  strategy: DistributionStrategy,
  holidayDates: ReadonlySet<string> = new Set(),
): { rows: PersonRow[]; unscheduled: UnscheduledIssue[] } {
  const demandIssues = issues.filter((i) => i.owner);

  const unscheduled: UnscheduledIssue[] = demandIssues
    .filter((i) => !i.startDate || !i.dueDate)
    .map((issue) => ({ issue, ownerName: issue.owner!.name }));

  const scheduled = demandIssues.filter((i) => i.startDate && i.dueDate);

  const rows: PersonRow[] = people.map((member) => {
    const capacityHours = member.allocatedHoursPerWeek ?? member.weeklyCapacityHours;
    const dailyCapacityHours = capacityHours / 5;
    const ownedIssues = scheduled.filter((i) => i.owner!.id === member.id);

    const perDay = new Map<string, number>();
    const itemsByDate = new Map<string, GridCellItem[]>();
    ownedIssues.forEach((issue) => {
      const dist = distributeIssueHours(issue, dailyCapacityHours, strategy, holidayDates);
      dist.forEach((hours, date) => {
        perDay.set(date, (perDay.get(date) ?? 0) + hours);
        const items = itemsByDate.get(date) ?? [];
        items.push({ issueId: issue.id, issueKey: issue.issueKey, issueName: issue.name, hours });
        itemsByDate.set(date, items);
      });
    });

    // Checks `holidayDates` directly rather than trusting `day.isHoliday` — `days` and
    // `holidayDates` are two independent params, and relying on a flag baked into `days` would
    // silently drift if a caller ever built it from a different holiday set than the one passed
    // here. `day.isWeekend` has no such risk (weekend-ness is intrinsic to the date itself).
    const cells: GridCell[] = days.map((day) => ({
      plannedHours: perDay.get(day.date) ?? 0,
      capacityHours: day.isWeekend || holidayDates.has(day.date) ? 0 : dailyCapacityHours,
      items: (itemsByDate.get(day.date) ?? []).slice().sort((a, b) => b.hours - a.hours),
    }));

    return { userId: member.id, name: member.name, avatarUrl: member.avatarUrl, capacityHours, cells };
  });

  return { rows, unscheduled };
}
